import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "./PropertyList.css";
import { FaEnvelope, FaPhone, FaEye, FaTrash } from "react-icons/fa";
import { hasAnyPermission } from "../../permissions";

// 🔧 Use the working backend URL
// const API_BASE = "https://revx-dgf-backend.onrender.com";
const API_BASE = "https://revxhospitality.in";// Retry a few times if backend is briefly unavailable
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000; // 2 seconds


const PropertyList = () => {
  const navigate = useNavigate();
  const handleBackToHome = () => {
  navigate("/HomePage");
};
  // PHASE 2 STARTS HERE 
const loggedInRole = localStorage.getItem("loggedInRole");
  const loggedInUser = localStorage.getItem("loggedInUser");

const canDeleteProperty = [
  "SUPER ADMIN",
  "ADMIN",
  "OTA MANAGER",
  "ONBOARDING MANAGER",
  "ONBOARDING EXECUTIVE",
].includes(loggedInRole) || hasAnyPermission(["read_write_properties_list"]);
// ENDS HERE

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterGroup, setFilterGroup] = useState("All");
  const [filterVisibility, setFilterVisibility] = useState("All");
  // Agent filter — see agentOptions below for where its values come from.
  const [filterAgent, setFilterAgent] = useState("All");
  // Property Name search — filters the table client-side as the user
  // types, same pattern as the Group/Agent/Model filters below.
  const [searchQuery, setSearchQuery] = useState("");
  // Agent Name filter/column options — usernames of every user in Settings
  // whose role is OTA EXECUTIVE or SALES EXECUTIVE (Sales Executive gets
  // the exact same treatment as OTA Executive, including being an
  // assignable agent). Fetched once on mount, independent of the
  // properties fetch below (this is a separate collection entirely).
  const [agentOptions, setAgentOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchAgents = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/users`);
        if (cancelled) return;

        const otaExecutives = (res.data || [])
          .filter((u) => u && u.username && ["OTA EXECUTIVE", "SALES EXECUTIVE"].includes(u.role))
          .map((u) => u.username);

        setAgentOptions(otaExecutives);
      } catch (err) {
        console.error("Failed to load agent list:", err);
      }
    };

    fetchAgents();

    return () => {
      cancelled = true;
    };
  }, []);

  // === LOAD PROPERTIES (with silent retry) ===
  useEffect(() => {
    let cancelled = false;

    const fetchWithRetry = async (attempt = 1) => {
      if (cancelled) return;

      setLoading(true);
      setError(null);

      try {
       const res = await axios.get(`${API_BASE}/api/properties`, {
          timeout: 15000, // matches backend maxTimeMS(15000) + headroom
        });

        if (cancelled) return;

        const mapped = (res.data || []).map((doc, index) => {
          let formattedDate = "";
          if (doc.createdAt) {
            const d = new Date(doc.createdAt);
            formattedDate = d.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
          }

          return {
            id: doc.id || doc._id || index + 1,
            propertyName: doc.propertyName || "",
            propertyType: doc.propertyType || "",
            propertyEmail: doc.propertyEmail || "",
            receptionNumber: doc.receptionNumber || "",
            marketingNumber: doc.marketingNumber || "",
            date: formattedDate,
            statusGroup: doc.statusGroup || "Onboard",
            visibilityGroup: doc.visibilityGroup || "REVX",
            assignedAgent: doc.assignedAgent || "",
            // raw: doc,
          };
        });

        setProperties(mapped);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;

        console.error(`Failed to load properties (attempt ${attempt}):`, err);

        if (attempt < MAX_RETRIES) {
          setTimeout(() => fetchWithRetry(attempt + 1), RETRY_DELAY_MS);
        } else {
          setError(
            "Unable to load properties right now. Please refresh again in a minute."
          );
          setLoading(false);
        }
      }
    };

    fetchWithRetry();

    return () => {
      cancelled = true;
    };
  }, []);

 const handleView = (propertyId) => {
  const selectedProperty = properties.find((p) => p.id === propertyId);

  // Navigate immediately for fast response
  navigate(`/property-details?id=${propertyId}`);

  // Save view log in background without blocking navigation
  axios
    .post(`${API_BASE}/api/edit-logs`, {
      changeMade: `Viewed property '${
        selectedProperty?.propertyName || "Unnamed Property"
      }'`,
      madeBy: loggedInUser || "Unknown",
      madeByRole: loggedInRole || "",
    })
    .catch((err) => {
      console.error("View property log failed:", err.response?.data || err);
    });
};

  // === UPDATE TAG HELPERS ===
  const updatePropertyTag = async (id, payload) => {
  try {
    const res = await axios.patch(
  `${API_BASE}/api/properties/${id}/tags`,
  {
    ...payload,
    changedBy: loggedInUser || "Unknown",
    changedByRole: loggedInRole || "",
  }
);

    const updatedProperty = res.data.property;

    if (updatedProperty) {
      setProperties((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                statusGroup: updatedProperty.statusGroup || p.statusGroup,
                visibilityGroup:
                  updatedProperty.visibilityGroup || p.visibilityGroup,
                assignedAgent:
                  updatedProperty.assignedAgent !== undefined
                    ? updatedProperty.assignedAgent
                    : p.assignedAgent,
              }
            : p
        )
      );
    }
  } catch (err) {
    console.error("Failed to update property tag:", err.response?.data || err);

    alert(
      err.response?.data?.message ||
        "Failed to update tag. Please check backend deployment."
    );
  }
};

  const handleGroupChange = (id, newGroup) => {
    setProperties((prev) =>
      prev.map((p) => (p.id === id ? { ...p, statusGroup: newGroup } : p))
    );
    updatePropertyTag(id, { statusGroup: newGroup });
  };

  const handleVisibilityChange = (id, newVisibility) => {
    setProperties((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, visibilityGroup: newVisibility } : p
      )
    );
    updatePropertyTag(id, { visibilityGroup: newVisibility });
  };

  const handleAgentChange = (id, newAgent) => {
    setProperties((prev) =>
      prev.map((p) => (p.id === id ? { ...p, assignedAgent: newAgent } : p))
    );
    updatePropertyTag(id, { assignedAgent: newAgent });
  };

 const handleDelete = async (id) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this property?"
  );

  if (!confirmDelete) return;

  try {
   await axios.delete(`${API_BASE}/api/properties/${id}`, {
  data: {
    deletedBy: loggedInUser,
    deletedByRole: loggedInRole,
  },
});

    setProperties((prev) => prev.filter((p) => p.id !== id));

    alert("Property deleted successfully.");
  } catch (err) {
    console.error("Failed to delete property:", err.response?.data || err);

    alert(
      err.response?.data?.message ||
        "Failed to delete property. Please check backend deployment."
    );
  }
};

  const filteredProperties = properties.filter(
    (p) =>
      (filterGroup === "All" || p.statusGroup === filterGroup) &&
      (filterVisibility === "All" || p.visibilityGroup === filterVisibility) &&
      (filterAgent === "All" || p.assignedAgent === filterAgent) &&
      (!searchQuery.trim() ||
        (p.propertyName || "").toLowerCase().includes(searchQuery.trim().toLowerCase()))
  );

  return (
  <div className="property-list-container">
    <div className="property-list-header">
      <h2 className="page-title">Property List</h2>

      <button
        type="button"
        className="property-home-btn"
        onClick={handleBackToHome}
      >
        Home
      </button>
    </div>

    {/* FILTERS */}
    <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          alignItems: "center",
          gap: "20px",
        }}
      >
        {/* Left-side filter group: Onboard/Offboard + Agent Name sit
            together here, so Agent Name shows up "on top left" alongside
            the existing filter rather than out on the right with Model. */}
        <div style={{ display: "flex", alignItems: "center", gap: "20px", flex: 1 }}>
          <div>
            <label
              style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}
            >
              Group by Onboard/Offboard:
            </label>
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="All">All</option>
              <option value="Onboard">Onboard</option>
              <option value="Offboard">Offboard</option>
            </select>
          </div>

          <div>
            <label
              style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}
            >
              Agent Name:
            </label>
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="All">All</option>
              {agentOptions.map((agent) => (
                <option key={agent} value={agent}>
                  {agent}
                </option>
              ))}
            </select>
          </div>

          {/* Property Name search — same label + input pattern as
              Group by Onboard/Offboard and Agent Name, so it lines up
              on the same baseline instead of floating unlabeled. */}
          <div>
            <label
              style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}
            >
              Property Name:
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search property name..."
              className="property-search-input"
              style={{
                padding: "8px 14px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                background: "#fff",
                width: "300px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        <div>
          <label
            style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}
          >
            Group by Model:
          </label>
          <select
            value={filterVisibility}
            onChange={(e) => setFilterVisibility(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            <option value="All">All</option>
            <option value="REVX">RevX</option>
            <option value="OVERALL">Overall</option>
            <option value="VISIBLE">Visible</option>
          </select>
        </div>
      </div>

      {/* ERROR MESSAGE (if any) */}
      {error && (
        <div style={{ marginBottom: "10px", color: "red" }}>{error}</div>
      )}

      {/* TABLE */}
      <div className="table-wrapper">
        <table className="property-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Property Name</th>
              <th>Information</th>
              <th>Category</th>
              <th>Date</th>
              <th>Group</th>
              <th>Agent</th>
              <th>Model</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredProperties.length > 0 && !loading ? (
              filteredProperties.map((item, index) => {
              

                return (
                  <tr key={item.id}>
                    <td>{index + 1}</td>

                    <td className="property-name">
                      {item.propertyName || "—"}
                    </td>

                    <td>
                      <div className="info-cell">
                        <FaPhone className="icon" />
                        <span>
                          {item.receptionNumber ||
                            item.marketingNumber ||
                            "—"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <span className="category-tag">
                        {item.propertyType || "—"}
                      </span>
                    </td>

                    <td>{item.date || "—"}</td>

                    {/* Group */}
                    <td>
                      <select
  className="group-select"
  value={item.statusGroup}
  onChange={(e) => handleGroupChange(item.id, e.target.value)}
  style={{
    backgroundColor:
      item.statusGroup === "Onboard" ? "#99fa99ff" : "#f17c7cff",
    color:
      item.statusGroup === "Onboard" ? "#106d00ff" : "#6f0000ff",
  }}
>
                        <option value="Onboard">Onboard</option>
                        <option value="Offboard">Offboard</option>
                      </select>
                    </td>

                    {/* Agent */}
                    <td>
                      <select
                        className="agent-select"
                        value={item.assignedAgent || ""}
                        onChange={(e) =>
                          handleAgentChange(item.id, e.target.value)
                        }
                        style={{
                          backgroundColor: item.assignedAgent
                            ? "#dbeafe"
                            : "#f3f4f6",
                          color: item.assignedAgent ? "#1e40af" : "#6b7280",
                        }}
                      >
                        <option value="">Unassigned</option>
                        {agentOptions.map((agent) => (
                          <option key={agent} value={agent}>
                            {agent}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Visibility */}
                    <td>
                      <select
  className="visibility-select"
  value={item.visibilityGroup}
  onChange={(e) => handleVisibilityChange(item.id, e.target.value)}
  style={{
    backgroundColor:
      item.visibilityGroup === "REVX"
        ? "#b2f2b6ff"
        : item.visibilityGroup === "OVERALL"
        ? "#ffd19dff"
        : "#9fcff9ff",
    color:
      item.visibilityGroup === "REVX"
        ? "#007902ff"
        : item.visibilityGroup === "OVERALL"
        ? "#924502ff"
        : "#352e7dff",
  }}
>
                        <option value="REVX">RevX</option>
                        <option value="OVERALL">Overall</option>
                        <option value="VISIBLE">Visible</option>
                      </select>
                    </td>

                    <td className="actions">
  <div className="actions-cell">
   <FaEye
  className="action-icon view"
  title="View"
  onClick={() => handleView(item.id)}
/>
    {canDeleteProperty && (
  <FaTrash
    className="action-icon delete"
    title="Delete"
    onClick={() => handleDelete(item.id)}
  />
)}
  </div>
</td>
                  </tr>
                );
              })
            ) : !loading && !error ? (
              <tr>
                <td
                  colSpan="9"
                  style={{
                    textAlign: "center",
                    color: "#888",
                    padding: "15px",
                  }}
                >
                  No properties found for selected filters.
                </td>
              </tr>
            ) : (
              // while loading, show spinner row
              <tr>
                <td
                  colSpan="9"
                  style={{
                    textAlign: "center",
                    padding: "18px",
                  }}
                >
                  <div className="table-loader">
                    <div className="spinner" />
                    <span>Loading…</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="copyright_footer">
        © 2026 Developed by{" "}
        <span className="brand">Vaibhav Prajapati </span> at{" "}
        <span style={{ color: "green" }}>RevX Hospitality</span>
      </div>
    </div>
  );
};

export default PropertyList;
